import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SnapshotList from './components/SnapshotList';
import RestoredInstances from './components/RestoredInstances';
import SecurityGroups from './components/SecurityGroups';
import RestoreModal from './components/RestoreModal';
import TakeSnapshotModal from './components/TakeSnapshotModal';
import InviteUserModal from './components/InviteUserModal';
import LoginPage from './components/LoginPage';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';

const AppContent = () => {
  const { isAuthenticated, loading, user, logout, awsCredentials } = useAuth();
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [mainTab, setMainTab] = useState('security-groups');
  const [rdsSubTab, setRdsSubTab] = useState('snapshots');
  const [showTakeSnapshotModal, setShowTakeSnapshotModal] = useState(false);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const handleSelectSnapshot = (snapshot) => {
    setSelectedSnapshot(snapshot);
  };

  const handleCloseModal = () => {
    setSelectedSnapshot(null);
  };

  const handleRestore = () => {
    setRdsSubTab('instances');
  };

  const handleTakeSnapshot = () => {
    setShowTakeSnapshotModal(true);
  };

  const handleCloseTakeSnapshotModal = () => {
    setShowTakeSnapshotModal(false);
  };

  const handleSnapshotCreated = () => {
    // Switch to manual tab to see the new snapshot
    setRdsSubTab('snapshots');
    // The SnapshotList component will refresh when the modal closes
  };

  return (
    <div>
      {/* AWS Header */}
      <div className="aws-header">
        <div className="aws-header-content">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className="aws-logo">AWS</span>
            <span className="aws-service-name">Developer Portal</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '14px', color: '#aab7b8' }}>
              {user?.email} | {process.env.REACT_APP_AWS_REGION || 'us-east-1'}
            </div>
            <button
              onClick={() => setShowInviteUserModal(true)}
              style={{
                background: 'transparent',
                border: '1px solid #aab7b8',
                color: '#aab7b8',
                padding: '5px 15px',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
              title="Invite a new user"
            >
              + Invite User
            </button>
            <button
              onClick={logout}
              style={{
                background: 'transparent',
                border: '1px solid #aab7b8',
                color: '#aab7b8',
                padding: '5px 15px',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>


      {/* Main Content */}
      <div className="aws-container">
        {/* Main Tabs (Security Groups / RDS) */}
        <div className="aws-tabs" style={{ marginBottom: '0' }}>
          <div className="aws-tab-headers" style={{ borderBottom: '2px solid #545b64' }}>
            <button
              className={`aws-tab-header ${mainTab === 'security-groups' ? 'active' : ''}`}
              onClick={() => setMainTab('security-groups')}
              style={{ fontSize: '16px', fontWeight: 'bold' }}
            >
              Security Groups
            </button>
            <button
              className={`aws-tab-header ${mainTab === 'rds' ? 'active' : ''}`}
              onClick={() => setMainTab('rds')}
              style={{ fontSize: '16px', fontWeight: 'bold' }}
            >
              RDS
            </button>
          </div>
        </div>

        {/* Security Groups Tab Content */}
        {mainTab === 'security-groups' && (
          <div>
            {/* Page Header */}
            <div className="aws-page-header">
              <h1 className="aws-page-title">Security Groups</h1>
              <p className="aws-page-description">
                Manage security group rules for your RDS instances. Add your current IP address or custom rules.
              </p>
            </div>

            <SecurityGroups awsCredentials={awsCredentials} />
          </div>
        )}

        {/* RDS Tab Content */}
        {mainTab === 'rds' && (
          <div>
            {/* Page Header */}
            <div className="aws-page-header">
              <h1 className="aws-page-title">RDS Database Management</h1>
              <p className="aws-page-description">
                Manage RDS database snapshots and instances. Restore databases from snapshots with one click.
              </p>
            </div>

            {/* RDS Sub Tabs */}
            <div className="aws-tabs">
              <div className="aws-tab-headers">
                <button
                  className={`aws-tab-header ${rdsSubTab === 'snapshots' ? 'active' : ''}`}
                  onClick={() => setRdsSubTab('snapshots')}
                >
                  Snapshots
                </button>
                <button
                  className={`aws-tab-header ${rdsSubTab === 'instances' ? 'active' : ''}`}
                  onClick={() => setRdsSubTab('instances')}
                >
                  Restored Instances
                </button>
              </div>

              <div className="aws-tab-content">
                {rdsSubTab === 'snapshots' && (
                  <SnapshotList
                    onSelectSnapshot={handleSelectSnapshot}
                    onTakeSnapshot={handleTakeSnapshot}
                    awsCredentials={awsCredentials}
                  />
                )}
                {rdsSubTab === 'instances' && (
                  <RestoredInstances awsCredentials={awsCredentials} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="aws-footer">
        <div className="aws-footer-content">
          Developed by <strong>Volodymyr Shynkar</strong> with <span className="aws-footer-heart">♥</span> to AWS
        </div>
      </div>

      {selectedSnapshot && (
        <RestoreModal
          snapshot={selectedSnapshot}
          onClose={handleCloseModal}
          onRestore={handleRestore}
          awsCredentials={awsCredentials}
        />
      )}

      {showTakeSnapshotModal && (
        <TakeSnapshotModal
          onClose={handleCloseTakeSnapshotModal}
          onSnapshotCreated={handleSnapshotCreated}
          awsCredentials={awsCredentials}
        />
      )}

      {showInviteUserModal && (
        <InviteUserModal
          onClose={() => setShowInviteUserModal(false)}
        />
      )}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;